import { motion, useReducedMotion } from 'framer-motion';

/**
 * NeevCloud brand mark — the company's real logo mark, traced from the
 * supplied logo-black.svg (72x72 viewBox, single path). Rendered with
 * `currentColor` so it goes white on this app's dark ground instead of the
 * source file's black; everything else about the geometry is unchanged.
 */
const MARK_PATH =
  'M50.6551 13.3731V1.04618C50.6551 0.468386 50.1867 0 49.609 0H22.3501C21.7723 0 21.3039 0.468386 21.3039 1.04618V20.2074C21.3039 20.7163 20.9475 21.1557 20.4495 21.2607L0.830368 25.3504C0.346392 25.4524 0 25.8794 0 26.374V70.9094C0 71.4872 0.468386 71.9556 1.04618 71.9556H20.2754C20.8532 71.9556 21.3216 71.4872 21.3216 70.9094V22.091C21.3216 21.582 21.678 21.1426 22.176 21.0376L49.3354 15.3113C50.0046 15.1702 50.634 15.6807 50.634 16.3646V70.9094C50.634 71.4872 51.1024 71.9556 51.6802 71.9556H70.9094C71.4871 71.9556 71.9555 71.4872 71.9555 70.9094V11.5451C71.9555 10.8804 71.3439 10.3843 70.6936 10.5214L51.9537 14.4265C51.2845 14.5675 50.6551 14.057 50.6551 13.3731Z';

export function NeevCloudMark({
  size = 28,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 72 72"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="NeevCloud"
    >
      <path d={MARK_PATH} fill="currentColor" className="text-mist-50" />
    </svg>
  );
}

/** Mark + wordmark, the primary lockup used on the login screen. */
export function NeevCloudLogo({
  size = 64,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <div className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.28 }}>
      <NeevCloudMark size={size} />
      <span
        className="font-bold text-mist-50"
        style={{ fontSize: size * 0.62, letterSpacing: '-0.02em', lineHeight: 1 }}
      >
        neevcloud
      </span>
    </div>
  );
}

/**
 * The brand reveal used on first load and right after sign-in. Deliberately
 * short and single-stage (fade + scale, no per-piece choreography) — the
 * mark should announce the product and get out of the way, not perform.
 */
export function BrandReveal({
  size = 120,
  play = true,
  className,
}: {
  size?: number;
  play?: boolean;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const animate = play && !reduce;

  return (
    <motion.div
      className={className}
      initial={animate ? { opacity: 0, scale: 0.8, y: 10 } : false}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <NeevCloudMark size={size} />
    </motion.div>
  );
}
